import { createHead } from '@unhead/vue/client';
import { createPinia } from 'pinia';
import { ConfirmationService, Tooltip } from 'primevue';
import PrimeVue from 'primevue/config';
import ToastService from 'primevue/toastservice';
import { createApp, h } from 'vue';
import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalizedLoaded,
} from 'vue-router';
import { routes } from 'vue-router/auto-routes';

import App from './App.vue';

import { activityCollector } from '@/analytics/ActivityCollector';
import { clearTokens, hasValidTokens } from '@/api/auth';
import { getUser } from '@/api/user';
import FullscreenSpinner from '@/components/FullscreenSpinner.vue';
import { useAuthStore } from '@/stores/auth';
import '@/styles/main.scss';
//@maxloo2 I don't see ENV_MODE used in the code, please check this
const ENV_MODE = import.meta.env.MODE;

// Boot overlay using the Vue spinner component
const bootHost = document.createElement('div');
bootHost.id = 'stw-boot-host';
document.body.appendChild(bootHost);
const bootApp = createApp({ render: () => h(FullscreenSpinner) });
bootApp.mount('#stw-boot-host');

const app = createApp(App);

// Dev-only: emits performance.mark/measure per component init/compile/render/patch,
// consumed by useVueRenderTiming.
// Vue compiles this instrumentation out of production bundles entirely, so this
// flag is a no-op there regardless - the DEV guard here is just for clarity.
if (import.meta.env.DEV) {
  app.config.performance = true;
}

const pinia = createPinia();

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 };
  },
});

router.beforeEach((to, from, next) => {
  if (window.location.hostname === 'stw-platform.web.app') {
    window.location.href = `https://scan-the-world.com${to.fullPath}`;
    return;
  }

  const redirectMap: Record<string, string> = {
    '/CUHK/metaverse':
      'https://static.scan-the-world.com/legacy/CUHK/metaverse/',
    '/STW/Builder': 'https://static.scan-the-world.com/legacy/STW/Builder.html',
    '/STW/HKEEIA': 'https://static.scan-the-world.com/legacy/STW/HKEEIA.html',
    '/dgs': 'https://static.scan-the-world.com/legacy/dgs/',
    '/dgs2': 'https://static.scan-the-world.com/legacy/dgs2/',
    '/CUHK/metaverse/':
      'https://static.scan-the-world.com/legacy/CUHK/metaverse/',
    '/STW/Builder/':
      'https://static.scan-the-world.com/legacy/STW/Builder.html',
    '/STW/HKEEIA/': 'https://static.scan-the-world.com/legacy/STW/HKEEIA.html',
    '/dgs/': 'https://static.scan-the-world.com/legacy/dgs/',
    '/dgs2/': 'https://static.scan-the-world.com/legacy/dgs2/',
  };

  const redirectUrl = redirectMap[to.path];
  if (redirectUrl) {
    window.location.href = redirectUrl;
    return;
  }

  // Admin route protection
  if (to.path.startsWith('/admin')) {
    const authStore = useAuthStore();
    const user = authStore.user;
    const isDevelopment = import.meta.env.MODE === 'development';

    // If auth hasn't initialized yet, allow navigation to proceed
    // The check will happen on subsequent navigations or the page will handle it
    if (!authStore.isInitialized) {
      if (isDevelopment) {
        console.log(
          '[Admin Route Guard] Auth not initialized yet, allowing navigation'
        );
      }
      next();
      return;
    }

    // Debug logging in development
    if (isDevelopment) {
      console.log('[Admin Route Guard]', {
        path: to.path,
        hasUser: !!user,
        isAdmin: user?.is_admin,
        mode: import.meta.env.MODE,
      });
    }

    // Check if user has access before routing to admin
    const hasAccess = user && (isDevelopment || user.is_admin);

    if (!hasAccess) {
      if (isDevelopment) {
        console.warn(
          '[Admin Route Guard] Access denied - User must be logged in to access admin in development'
        );
      }
      next('/');
      return;
    }
  }

  if (to.matched.length === 0) {
    next('/');
  } else {
    next();
  }
});

// `route.matched[...].path` is the route's *template*, not the resolved path,
// so the activity batch's `route` field stays free of dynamic ids.
const toActivityRouteFields = (route: RouteLocationNormalizedLoaded) => {
  const template = route.matched[route.matched.length - 1]?.path ?? route.path;
  const params: Record<string, string> = {};
  for (const [key, value] of Object.entries(route.params)) {
    params[key] = Array.isArray(value) ? value.join(',') : value;
  }
  return { template, params };
};

// This is an SPA, so navigating never fires visibilitychange/pagehide - flush
// for `from` (the page being left), then store `to` as the current route.
router.afterEach((to, from) => {
  const fromFields = toActivityRouteFields(from);
  activityCollector.flush(fromFields.template, fromFields.params);

  const toFields = toActivityRouteFields(to);
  activityCollector.setCurrentRoute(toFields.template, toFields.params);
});

app.use(router);

app.use(pinia);

app.use(PrimeVue, {
  theme: 'none',
});
app.use(ToastService);
app.use(ConfirmationService);
app.directive('tooltip', Tooltip);

const head = createHead();
app.use(head);

// Main initialization
(async () => {
  const authStore = useAuthStore();
  let isAppMounted = false;

  const mountApp = () => {
    if (isAppMounted) return;
    app.mount('#app');
    isAppMounted = true;
    bootApp.unmount();
    bootHost.remove();
  };

  try {
    // Check if user has valid JWT tokens and fetch user data if so
    if (hasValidTokens()) {
      try {
        const user = await getUser();
        await authStore.setUser(user);
      } catch (error) {
        console.warn(
          '[main.ts] Failed to fetch user using stored JWT tokens',
          error
        );
        // Keep tokens on network failure (backend down); clear on any other error
        if (!(error instanceof TypeError)) clearTokens();
      }
    }

    // Mark auth as initialized
    authStore.setInitialized();

    // Always mount the app
    mountApp();
  } catch (error) {
    console.warn('[main.ts] App initialization error:', error);
    // Mark auth as initialized even if it fails
    authStore.setInitialized();
    // Still mount app even if auth fails
    mountApp();
  }
})();
